<template>
  <div
    class="product-table__no-products-found"
    v-if="products?.count===0 && !props.resetInProgress"
  >
    <h3 :class="{ 'is-ccrs-headline': props.usePopup }">
      {{ $tv({ messageKey: headerKey, messageValues: { total: resultsMessage } }, props.usePopup ? 'ccrs' : 'categories') }}
    </h3>
    <div
      class="product-table__no-products-found__headline"
      :class="{'is-ccrs-headline': props.usePopup}"
    />
    <div>
      <div
        class="product-table__no-products-found__content"
        :class="{'is-ccrs-content': props.usePopup}"
        v-show="shouldShowNoProducts && products.count === 0"
      >
        <NoProductsToShow
          class="show_no_products_icon"
        />
        <h4
          class="product-table__no-products-found__content__headline"
          v-if="props.usePopup"
        >
          {{ $tv('showNoProductsCcrs', 'categories') }}
        </h4>

        <div
          class="product-table__no-products-found__content__description"
          :class="{'is-ccrs-styled': props.usePopup}"
        >
          {{ $tv(props.usePopup ? 'showNoProductsCcrsDescription' : 'showNoProducts', props.usePopup ? 'ccrs' : 'categories') }}
        </div>
        <div class="product-table__no-products-found__content__button">
          <ReactiveButton
            :is-primary-button="false"
            @click="props.usePopup ? openPopup() : handleContactClick()"
          >
            <div class="product-table__no-products-found__content__button__item">
              <span>
                {{ $tv('Contact', 'footer') }}
              </span>
              <ArrowDown v-if="!props.usePopup" />
            </div>
          </ReactiveButton>
        </div>
      </div>
      <div
        v-show="shouldShowResetFilters && products.count === 0"
        class="product-table__no-products-found__content"
        :class="{'is-ccrs-content': props.usePopup}"
      >
        <ResetFilters
          class="show_no_products_icon"
        />
        <div class="product-table__no-products-found__content__description">
          {{ $tv(props.usePopup ? 'noProductsFoundResetCcrs' : 'noProductsFoundReset', 'categories') }}
        </div>
        <div class="product-table__no-products-found__content__button">
          <ReactiveButton
            :button-text="$tv(props.usePopup ? 'reset-filter-ccrs' : 'reset-filter', 'categories')"
            :is-primary-button="false"
            @click="resetFilters"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {ref, computed, nextTick, watch, type PropType} from 'vue'
import NoProductsToShow from '~/assets/icons/no_products_to_show.svg?component'
import ArrowDown from '~/assets/icons/md_arrow_downward.svg?component'
import ResetFilters from '~/assets/icons/no_products_found.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'

const i18n = useI18nExtended()

const app = useNuxtApp()

type ProductsProp = {count: number} | null

const props = defineProps({
  products: {
    type: Object as PropType<ProductsProp>,
    required: true
  },
  hasActiveValues: {
    type: Boolean,
    default: false
  },
  usePopup: {
    type: Boolean,
    default: false
  },
  searchQuery: {
    type: [String, Object],
    default: ''
  },
  resetInProgress: {
    type: Boolean,
    default: false
  },
  ccrsType: {
    type: String,
    required: false,
    default: 'design'
  }
})
const showNoProducts = ref(false)
const showResetFilters = ref(false)

// Computed properties to determine which content to show
const shouldShowNoProducts = computed(() => {
  return !props.hasActiveValues && !props.resetInProgress && showNoProducts.value
})

const shouldShowResetFilters = computed(() => {
  return props.hasActiveValues && !props.resetInProgress && showResetFilters.value
})

const resultsMessage = computed(() => {
  const locale = i18n.locale.value === 'en' ? 'en' : 'de'
  return props.products?.count?.toLocaleString(locale)
})
const headerKey = computed(() => {
  if (props.usePopup) {
    return props.ccrsType === 'design' ? 'resultsDesignHeader' : 'ccrsProductHeader'
  }
  return 'results'
})
const emit = defineEmits(['resetFilters', 'contact-click'])

const handleContactClick = () => {
  // Open contact form popup
  const eventbus = app.$eventbus as any
  eventbus.emit('showContactPopup', {topicId: '1e53b8d6c40045a1b3eca1d70eb29350'})
  emit('contact-click')
}

const openPopup = () => {
  const query = (props.searchQuery as any)?.value ?? props.searchQuery ?? ''
  const prefilled = i18n.tv('searchQueryLabel', 'ccrs') + '\n--------\n' + query
  const eventbus = app.$eventbus as any
  eventbus.emit('showContactPopup', {
    topicId: '1e53b8d6c40045a1b3eca1d70eb29350',
    prefilledMessage: prefilled
  })
  emit('contact-click')
}

const resetFilters = () => {
  emit('resetFilters')
}

watch(
  () => [props.hasActiveValues, props.resetInProgress],
  ([hasActiveValues, resetInProgress]) => {
    if (resetInProgress) {
      // During reset process, hide both states to prevent flashing
      showNoProducts.value = false
      showResetFilters.value = false
    } else {
      // After reset is complete or when filters change normally
      // Use nextTick to ensure smooth transition
      nextTick(() => {
        showNoProducts.value = !hasActiveValues
        showResetFilters.value = hasActiveValues
      })
    }
  },
  {immediate: true}
)
</script>
<style lang="scss">
.product-table {
  position: relative;
  min-height: 15.625rem;
  padding-top: .5rem;

  &__no-products-found {
    &__headline {
      height: 2.5rem;
      background-color: $color__fst__primary;
      margin: 0 2rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    .is-ccrs-headline {
      margin: 0;
    }

    h3 {
      margin: 0 2rem;
      padding-bottom: .5rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__content {
      display: flex;
      align-items: center;
      flex-direction: column;
      background-color: $color__fst__white;
      margin: 0 2rem;

      .show_no_products_icon {
        padding-top: 1.25rem;
      }

      .icon.icon--svg {
        width: 9.375rem;
        height: 9.375rem;
      }

      &__description {
        padding: 1rem;
      }

      .is-ccrs-styled{
        padding: 0 1rem 1rem;
        font-weight: 300;
      }

      &__button {
        margin-bottom: 1.5rem;

        &__item {
          display: flex;
          gap: .2rem;
          align-content: center;

          span {
            padding-top: .12rem;
          }

          .icon.icon--svg {
            width: 1.5rem;
            height: 1.55rem;
          }
        }
      }

      &__headline {
        padding: 0 1rem;
      }

      h4 {
        margin: 1rem 0 0;
        font-weight: 600;
        color: $color__fst__black
      }

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    .is-ccrs-content {
      margin: 0;
    }

    &__contact-form {
      display: flex;
      justify-content: center;
      padding: 3rem;

      &__form {
        max-width: 40rem;
      }
    }
  }
}
</style>
